/**
 * PotentialFlexibilityChart - Shows the community's potential flexibility capacity over 24 hours.
 *
 * Props:
 *   - hourlyData: Array of 24 objects with { total_generation, total_load, total_battery_charge, surplus_kw }
 *
 * Displays:
 *   - Area chart showing total shiftable/flexible capacity (kW) per hour
 *   - Comparison of "current schedule" vs "optimal schedule"
 *   - Surplus (positive) = available for grid export or additional demand response
 *   - Deficit (negative) = grid import needed
 *
 * Color coding: green for surplus (export potential), red for deficit (import needed)
 */

import React, { useMemo } from 'react'
import Chart from 'react-apexcharts'

// Generate mock flexibility data
const generateMockFlexibilityData = () => {
  const hours = Array.from({ length: 24 }, (_, i) => i)

  return hours.map((h) => {
    // Mock generation: solar PV bell curve + some wind
    let generation = 0
    if (h >= 6 && h <= 18) {
      const hourFromNoon = Math.abs(h - 12)
      generation = Math.max(0, 15 * Math.exp(-0.08 * hourFromNoon * hourFromNoon))
    }
    generation += Math.random() * 3 + 1 // Wind: 1-4 kW

    // Mock load: residential pattern
    let load = 2.5 // Base
    if (h >= 6 && h <= 9) load += 3.0 // Morning peak
    if (h >= 17 && h <= 22) load += 4.5 // Evening peak
    if (h >= 9 && h <= 17) load += 2.0 // Day base

    // Mock battery charge (during excess PV, 10am-3pm)
    let batteryCharge = 0
    if (h >= 10 && h <= 15 && generation > load) {
      batteryCharge = Math.min((generation - load) * 0.4, 2)
    }

    // Surplus calculation: generation - load - battery_charge
    const surplus = generation - load - batteryCharge

    return {
      hour: h,
      total_generation: generation,
      total_load: load,
      total_battery_charge: batteryCharge,
      surplus_kw: surplus,
    }
  })
}

// Generate "optimal schedule" where flexibility is redistributed to solar peak hours
const generateOptimalSchedule = (baseline) => {
  return baseline.map((item, h) => {
    // Optimal: shift more load to solar peak hours (10-15) and overnight (0-6)
    let adjustedLoad = item.total_load
    if (h >= 10 && h <= 15) {
      // Increase load during solar peak (shift flexible loads here)
      adjustedLoad += 2.0
    }
    if (h >= 0 && h <= 6) {
      // Increase overnight load (EV charging, etc.)
      adjustedLoad += 1.5
    }
    if (h >= 17 && h <= 22) {
      // Reduce evening peak load (defer flexible loads)
      adjustedLoad = Math.max(adjustedLoad - 2.5, item.total_load * 0.7)
    }

    const adjustedSurplus = item.total_generation - adjustedLoad - item.total_battery_charge

    return {
      ...item,
      optimal_load: adjustedLoad,
      optimal_surplus: adjustedSurplus,
    }
  })
}

function PotentialFlexibilityChart({ hourlyData }) {
  const usingRealData = hourlyData?.length > 0

  // Show empty state when no real data is available
  if (!usingRealData) {
    return (
      <div className="potential-flexibility-chart-container">
        <h6 style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, marginBottom: 'var(--space-3)', color: 'var(--color-neutral-700)' }}>
          Potential Flexibility Capacity
        </h6>
        <div className="text-center py-4" style={{ color: 'var(--color-neutral-500)' }}>
          <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, margin: 0 }}>No flexibility data available</p>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-400)', marginTop: 'var(--space-2)' }}>
            Run an optimization with flexible assets to see potential flexibility capacity.
          </p>
        </div>
      </div>
    )
  }

  // Use real optimizer data to build baseline vs optimal comparison
  const baseline = hourlyData
  const optimal = generateOptimalSchedule(hourlyData)

  const hours = baseline.map((item) => item.hour ?? item.h ?? baseline.indexOf(item))
  const baselineSurplus = baseline.map((item) => item.surplus_kw ?? item.optimal_surplus ?? 0)
  const optimalSurplus = optimal.map((item) => item.optimal_surplus ?? item.surplus_kw ?? 0)

  // ApexCharts configuration
  const chartOptions = {
    chart: {
      type: 'area',
      height: 350,
      stacked: false,
      toolbar: { show: true },
      animations: { enabled: true, speed: 800 },
    },
    colors: ['#95A5A6', '#27AE60'], // Gray for baseline, green for optimal
    dataLabels: { enabled: false },
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    fill: {
      type: 'solid',
      opacity: 0.4,
    },
    legend: {
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '12px',
      markers: { width: 10, height: 10, radius: 2 },
    },
    xaxis: {
      categories: hours,
      title: { text: 'Hour of Day', style: { fontSize: '12px', fontWeight: 500 } },
      labels: { style: { fontSize: '11px' } },
    },
    yaxis: {
      title: {
        text: 'Flexible Capacity (kW)',
        style: { fontSize: '12px', fontWeight: 500 },
      },
      labels: {
        style: { fontSize: '11px' },
        formatter: (value) => value != null ? value.toFixed(1) : '',
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (value) => value != null ? `${value.toFixed(2)} kW` : '',
      },
    },
    grid: {
      borderColor: '#e0e0e0',
      strokeDashArray: 3,
    },
    annotations: {
      yaxis: [
        {
          y: 0,
          borderColor: '#666',
          strokeDashArray: 2,
          label: {
            text: 'Zero Line',
            style: { fontSize: '10px', color: '#666' },
          },
        },
      ],
    },
  }

  const series = [
    {
      name: usingRealData ? 'Without Optimization (Baseline)' : 'Current Schedule (Surplus)',
      data: baselineSurplus,
    },
    {
      name: usingRealData ? 'With Optimization (Actual)' : 'Optimal Schedule (Surplus)',
      data: optimalSurplus,
    },
  ]

  return (
    <div className="potential-flexibility-chart-container">
      <h6
        style={{
          fontSize: 'var(--font-size-base)',
          fontWeight: 600,
          marginBottom: 'var(--space-3)',
          color: 'var(--color-neutral-700)',
        }}
      >
        Potential Flexibility Capacity
      </h6>
      <p
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-neutral-600)',
          marginBottom: 'var(--space-3)',
        }}
      >
        Hourly surplus generation after covering loads and battery charging. Positive values indicate
        export potential or additional demand response capacity. Negative values indicate grid import needed.
      </p>

      <Chart options={chartOptions} series={series} type="area" height={350} />

      {/* Legend explanation */}
      <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
        <div className="d-flex gap-4">
          <div>
            <span style={{ fontWeight: 600, color: 'var(--color-success)' }}>Positive values:</span> Surplus generation
            available for export or additional flexible loads
          </div>
          <div>
            <span style={{ fontWeight: 600, color: 'var(--color-danger)' }}>Negative values:</span> Grid import needed
            to meet demand
          </div>
        </div>
      </div>
    </div>
  )
}

export default PotentialFlexibilityChart
